import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Heart, MapPin } from "lucide-react";
import Sidebar from "./components/Sidebar";
import BottomNav from "./components/BottomNav";
import MapViewLite, { MarkerData } from "./components/MapViewLite";
import { getImageVariants } from "./utils/imageUtils";
import { useSavedPlaces } from "./contexts/SavedPlacesContext";

export default function Layout({
  mapMarkers,
  highlightedId,
  setHighlightedId,
  hoveredId,
  onMapMarkerClick,
  carouselState,
  carouselHandlers,
  carouselData,
}: {
  mapMarkers: MarkerData[];
  highlightedId: string | number | null;
  setHighlightedId: (id: string | number | null) => void;
  hoveredId?: string | number | null; // For hover highlighting (visual only, no centering)
  onMapMarkerClick?: (id: string | number) => void;
  carouselState?: { activeCardIndex: number; cardSwipeOffset: number; isCardSwiping: boolean };
  carouselHandlers?: any;
  carouselData?: any[];
}) {
  // ðŸ'‡ state to toggle between "content" and "map" on mobile
  const [activeMobileTab, setActiveMobileTab] = useState<"content" | "map">("content");
  const location = useLocation();
  
  
  // Get saved places functions
  const { isPlaceSaved, savePlace, unsavePlace } = useSavedPlaces();
  
  // Check if we're on the explore page - it handles its own mobile layout
  const isExplorePage = location.pathname.includes('/explore');
  // Check if we're on the saved places page - it has its own integrated map
  const isSavedPage = location.pathname.includes('/saved');
  // Check if we're on the trips page - it has its own layout
  const isTripsPage = location.pathname.includes('/trips');
  // Check if we're on the plan page - it has its own Mapbox map
  const isPlanPage = location.pathname.includes('/plan');
  // Check if we're on the profile page - no map needed
  const isProfilePage = location.pathname.includes('/profile');
  // Check if we're on the services page - no map needed
  const isServicesPage = location.pathname.includes('/services');
  // Check if we're on the featured page - no map needed
  const isFeaturedPage = location.pathname.includes('/featured');
  // Check if we're on the chat page - it should show a clean map without empty state message
  const isChatPage = location.pathname.includes('/chat');

  return (
    <div className="h-screen-safe flex overflow-hidden">
      {/* Sidebar (desktop only) */}
      <Sidebar />

      {/* Main content area - with safe area insets for iOS notch/home indicator */}
      <div className="flex-1 flex flex-col overflow-hidden relative pb-16 md:pb-0" style={{ paddingTop: 'max(0px, env(safe-area-inset-top))', paddingLeft: 'max(0px, env(safe-area-inset-left))', paddingRight: 'max(0px, env(safe-area-inset-right))' }}>
        {/* --- Mobile floating toggle (only for chat page, not on profile/explore/saved/trips/services/featured) --- */}
        {isChatPage && !isProfilePage && !isServicesPage && !isFeaturedPage && activeMobileTab === "content" && (
          <button
            className="md:hidden fixed top-2 left-1/2 -translate-x-1/2 z-40 
                       bg-black rounded-full px-4 py-2 shadow-lg text-white text-sm font-medium
                       hover:shadow-xl transition-all active:scale-95"
            onClick={() => setActiveMobileTab("map")}
          >
            Show Map
          </button>
        )}

        {isChatPage && !isProfilePage && !isServicesPage && !isFeaturedPage && activeMobileTab === "map" && (
          <button
            className="md:hidden fixed top-2 left-1/2 -translate-x-1/2 z-40 
                       bg-black rounded-full px-4 py-2 shadow-lg text-white text-sm font-medium
                       hover:shadow-xl transition-all active:scale-95"
            onClick={() => setActiveMobileTab("content")}
          >
            Show Chat
          </button>
        )}

        {/* --- Desktop Layout: Chat gets 50/50 split, Explore, Saved, Trips, Plan, Services, Featured, and Profile get full width --- */}
        <div className={`flex-1 flex overflow-hidden ${(isExplorePage || isSavedPage || isTripsPage || isPlanPage || isProfilePage || isServicesPage || isFeaturedPage) ? 'flex-col' : 'md:flex-row'}`}>
          {/* --- Content area (routes) --- */}
          <div
            className={`${
              (isExplorePage || isSavedPage || isTripsPage || isPlanPage || isProfilePage || isServicesPage || isFeaturedPage)
                ? "flex-1 overflow-y-auto" 
                : `flex-1 overflow-hidden ${activeMobileTab === "content" ? "block" : "hidden"} md:block`
            }`}
          >
            <Outlet />
          </div>

          {/* --- Desktop Map (only for non-explore, non-saved, non-trips, non-plan, non-services, non-featured, and non-profile pages on desktop) --- */}
          {!isExplorePage && !isSavedPage && !isTripsPage && !isPlanPage && !isProfilePage && !isServicesPage && !isFeaturedPage && (
            <div className="hidden md:block w-1/2 overflow-hidden">
              <MapViewLite
                markers={mapMarkers}
                highlightedId={highlightedId}
                hoveredMarkerId={hoveredId}
                onMarkerClick={(id) => {
                  setHighlightedId(id);
                  onMapMarkerClick?.(id);
                }}
                className="h-full"
                showEmptyState={!isChatPage}
                interactive={true}
              />
            </div>
          )}
        </div>

        {/* --- Mobile Map (only on mobile when map tab is active, and not on explore, saved, trips, plan, services, featured, or profile pages) --- */}
        {!isExplorePage && !isSavedPage && !isTripsPage && !isPlanPage && !isProfilePage && !isServicesPage && !isFeaturedPage && (
          <div
            className={`md:hidden ${activeMobileTab === "map" ? "block" : "hidden"} absolute inset-0 overflow-hidden`}
            style={{ top: 0, bottom: '64px' }} // Account for bottom nav height (64px)
          >
            <MapViewLite
              markers={mapMarkers}
              highlightedId={highlightedId}
              hoveredMarkerId={hoveredId}
              onMarkerClick={(id) => {
                setHighlightedId(id);
                onMapMarkerClick?.(id);
              }}
              className="h-full"
              showEmptyState={!isChatPage}
              interactive={true}
            />
            
            {/* Mobile Map Carousel - Only shown in map view */}
            {carouselData && carouselData.length > 0 && carouselState && carouselHandlers && (() => {
              return (
              <div className="absolute left-0 right-0 overflow-visible pointer-events-none z-20" style={{ top: '2.5rem' }}>
                <div 
                  className="flex gap-3 pointer-events-auto"
                  style={{
                    paddingLeft: 'calc(50vw - 140px)',
                    paddingRight: 'calc(50vw - 140px)',
                    // Carousel with duplicate cards for infinite loop
                    transform: carouselData.length > 1
                      ? `translateX(calc(-${(carouselState.activeCardIndex + 1) * 292}px + ${carouselState.cardSwipeOffset}px))`
                      : `translateX(${carouselState.cardSwipeOffset}px)`,
                    transition: carouselState.isCardSwiping ? 'none' : 'transform 0.3s ease-out',
                    touchAction: 'none' // Prevent default touch behaviors like pull-to-refresh
                  }}
                  onTouchStart={carouselHandlers.handleCardTouchStart}
                  onTouchMove={carouselHandlers.handleCardTouchMove}
                  onTouchEnd={carouselHandlers.handleCardTouchEnd}
                >
                  {/* Carousel cards */}
                  {(() => {
                    // Create carousel array with duplicates for infinite loop
                    const carouselMarkers = carouselData.length > 1 
                      ? [carouselData[carouselData.length - 1], ...carouselData, carouselData[0]]
                      : carouselData;
                    
                    return carouselMarkers.map((marker: any, carouselIndex: number) => {
                      // Calculate the actual index in the original array
                      const actualIndex = carouselData.length > 1
                        ? (carouselIndex === 0 ? carouselData.length - 1 
                          : carouselIndex === carouselMarkers.length - 1 ? 0 
                          : carouselIndex - 1)
                        : carouselIndex;
                      
                      const isActive = actualIndex === carouselState.activeCardIndex;
                      
                      // Determine type color
                      const typeConfig: { [key: string]: { bgColor: string; color: string; code: string } } = {
                        attraction: { bgColor: 'bg-blue-500', color: 'text-white', code: 'A' },
                        food: { bgColor: 'bg-orange-500', color: 'text-white', code: 'F' },
                        hotel: { bgColor: 'bg-green-500', color: 'text-white', code: 'H' },
                        experience: { bgColor: 'bg-purple-500', color: 'text-white', code: 'E' }
                      };
                      const currentTypeConfig = typeConfig[marker.type] || typeConfig['attraction'];
                      
                      return (
                        <div
                          key={`carousel-${marker.id}-${carouselIndex}`}
                          className={`flex-shrink-0 bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 cursor-pointer ${
                            isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                          }`}
                          style={{ 
                            width: '280px'
                          }}
                          onClick={() => {
                            if (!isActive) {
                              // Call navigateToCard which handles carousel centering and marker highlighting
                              if (carouselHandlers?.navigateToCard) {
                                carouselHandlers.navigateToCard(actualIndex);
                              }
                              // Also directly highlight the marker to ensure it works
                              if (carouselHandlers?.setHighlightedId && carouselData[actualIndex]) {
                                const markerId = carouselData[actualIndex].id;
                                carouselHandlers.setHighlightedId(markerId);
                              }
                            }
                          }}
                        >
                          <div className="flex h-24">
                            {/* Image on Left */}
                            <div 
                              className="relative w-24 flex-shrink-0 bg-gradient-to-br from-gray-200 to-gray-300"
                            >
                              {marker.photo_1_cdn && (() => {
                                const variants = getImageVariants(marker.photo_1_cdn);
                                const imageUrl = variants.thumb || variants.tiny || variants.original;
                                return (
                                  <img 
                                    src={imageUrl || ''} 
                                    alt={marker.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                );
                              })()}
                              {/* Type Badge */}
                              <div className={`absolute bottom-1 left-1 ${currentTypeConfig.bgColor} px-1.5 py-0.5 rounded-full flex items-center gap-0.5`}>
                                <span className={`text-[10px] font-bold ${currentTypeConfig.color}`}>{currentTypeConfig.code}</span>
                              </div>
                            </div>

                            {/* Content on Right */}
                            <div className="flex-1 p-2.5 flex flex-col justify-between min-w-0">
                              {/* Top: Title and Heart Button */}
                              <div>
                                <div className="flex items-start justify-between gap-1.5 mb-1">
                                  <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2 flex-1">
                                    {marker.title}
                                  </h3>
                                  {(() => {
                                    const isSaved = marker.id ? isPlaceSaved(marker.id as string) : false;
                                    return (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (marker.id) {
                                            if (isSaved) {
                                              unsavePlace(marker.id as string).catch(() => {});
                                            } else {
                                              savePlace(marker as any).catch(() => {});
                                            }
                                          }
                                        }}
                                        className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
                                          isSaved
                                            ? 'bg-red-50'
                                            : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                        title={isSaved ? 'Remove from saved' : 'Save place'}
                                      >
                                        <Heart className={`w-3.5 h-3.5 ${isSaved ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                                      </button>
                                    );
                                  })()}
                                </div>

                                {/* Location */}
                                <div className="flex items-center gap-1 text-xs text-gray-600">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{marker.title}</span>
                                </div>
                              </div>

                              {/* Rating at Bottom */}
                              {marker.rating && (
                                <div className="flex items-center gap-0.5 bg-yellow-50 px-1.5 py-0.5 rounded w-fit mt-1">
                                  <span className="text-yellow-500 text-xs">★</span>
                                  <span className="text-xs font-semibold text-gray-900">
                                    {typeof marker.rating === 'number' ? marker.rating.toFixed(1) : marker.rating}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Bottom nav (mobile only) */}
      <BottomNav />
    </div>
  );
}
